import type { TranscriptItem } from '../src/types/visual.js'
import type { ResearchResult } from '../src/core/intelligence/lead-research.js'
import type { IntelligenceContext } from '../src/core/agents/types.js'
import { logger } from '../src/lib/logger.js'
import {
  clearCachedDiscoveryReport,
  mergeCachedDiscoveryReportIntoTranscript,
} from '../src/lib/report-preview-cache.js'
import { stripHypothesisInstructionLeak } from '../src/core/agents/utils/working-hypothesis.js'
import { stripLiveVisualDirective } from '../src/lib/live-visual-directive.js'
import { visualObservationModalityOfStoredNote } from '../src/lib/visual-observation-store.js'
import { mergeLiveOptimisticTranscript } from '../src/lib/live-transcript-reconciler.js'
import {
  createDefaultLiveSessionPolicy,
  mergeLiveSessionPolicy,
  type LiveSessionPolicy,
} from '../src/types/live-policy.js'
import { ClientSessionStore } from '../src/core/session/client-session-store.js'
import type {
  CanonicalSessionProjectionV1,
  SessionProjectionEnvelopeV1,
} from '../src/core/session/session-projection.js'
import {
  isSupportedUiLocale,
  UI_LOCALE_STORAGE_KEY,
  uiLocaleToLanguageCode,
  type UiLocale,
} from '../src/core/contracts.js'
import {
  canonicalProjectionToIntelligenceContext,
  canonicalProjectionToConversationFlow,
  flatContextRecord,
} from '../src/lib/canonical-session-adapters.js'

type Location = { latitude: number; longitude: number }

type VisualAnalysisSummary = {
  analysis: string
  type: string        // 'webcam' | 'screen' | 'upload'
  timestamp: string
  confidence?: number
  source?: string     // 'webcam' | 'screenshare'
}

type MultimodalSnapshot = {
  visualAnalyses: VisualAnalysisSummary[]
  hasActiveCamera: boolean
  hasActiveScreenShare: boolean
}

export type ClientSessionViewState = {
  sessionId?: string
  researchContext?: ResearchResult | null
  location?: Location
  conversationFlow?: unknown
  intelligenceContext?: IntelligenceContext
  transcript: TranscriptItem[]
  language?: string // Preferred language (e.g., 'en', 'nb', 'es')
  uiLocale?: UiLocale
  uiLocaleExplicit?: boolean
  livePolicy?: LiveSessionPolicy
  multimodalContext?: MultimodalSnapshot
  sessionPermissions?: {
    voice: boolean
    webcam: boolean
    screen: boolean
    files: boolean
    location: boolean
    research: boolean
  }
}

type Listener = (state: ClientSessionViewState) => void

interface StoredUiPreferences {
  language?: string
  uiLocale?: UiLocale
  uiLocaleExplicit?: boolean
  livePolicy?: LiveSessionPolicy
}

function deriveTranscript(projection: CanonicalSessionProjectionV1): TranscriptItem[] {
  return projection.transcript
    .filter(entry => entry.role === 'user' || entry.role === 'assistant')
    .map((entry): TranscriptItem => {
      const text = stripHypothesisInstructionLeak(stripLiveVisualDirective(entry.content))
      const noteModality =
        entry.role === 'user' && entry.modality !== 'voice'
          ? visualObservationModalityOfStoredNote(text)
          : null
      if (noteModality) {
        // Stored camera/screen note: a system notice, never a YOU row.
        return {
          id: entry.entryId,
          role: 'model',
          text: text.trim(),
          timestamp: new Date(entry.occurredAt),
          isFinal: true,
          status: 'complete',
          isLiveTranscript: false,
          attachment: {
            type: 'visual_observation',
            name: noteModality === 'screen' ? 'Screen observation' : 'Camera observation',
            data: `${noteModality}:success`,
          },
        }
      }
      return {
        id: entry.entryId,
        role: entry.role === 'assistant' ? 'model' : 'user',
        text,
        timestamp: new Date(entry.occurredAt),
        isFinal: entry.state !== 'interim',
        turnId: entry.turnId,
        ...(entry.modality === 'voice' ? { isLiveTranscript: true as const } : {}),
      }
    })
    .filter(item => item.text.trim().length > 0)
}

function deriveIntelligenceContext(
  projection: CanonicalSessionProjectionV1
): IntelligenceContext {
  return canonicalProjectionToIntelligenceContext(projection)
}

function deriveResearchContext(
  projection: CanonicalSessionProjectionV1
): ResearchResult | null {
  const research = flatContextRecord(projection.researchContext)
  return Object.keys(research).length > 0 ? (research as unknown as ResearchResult) : null
}

function deriveConversationFlow(projection: CanonicalSessionProjectionV1): Record<string, unknown> {
  const userEntries = projection.transcript.filter(entry => entry.role === 'user')
  const firstUserAt = userEntries[0]?.occurredAt
  const latestUserAt = userEntries[userEntries.length - 1]?.occurredAt

  return {
    covered: {
      goals: false,
      pain: false,
      data: false,
      readiness: false,
      budget: false,
      success: false,
    },
    recommendedNext: null,
    evidence: {},
    insights: {},
    coverageOrder: [],
    ...canonicalProjectionToConversationFlow(projection),
    totalUserTurns: projection.userMessageCount,
    firstUserTimestamp: firstUserAt ? Date.parse(firstUserAt) : null,
    latestUserTimestamp: latestUserAt ? Date.parse(latestUserAt) : null,
    shouldOfferRecap: projection.stage === 'SUMMARY',
  }
}

function deriveMultimodalContext(projection: CanonicalSessionProjectionV1): MultimodalSnapshot {
  return {
    visualAnalyses: projection.evidence
      .filter(
        evidence =>
          evidence.status === 'verified' &&
          (evidence.kind === 'screen' || evidence.kind === 'webcam')
      )
      .map(evidence => ({
        analysis: evidence.summary,
        type: evidence.kind,
        timestamp: evidence.capturedAt,
        ...(evidence.confidence !== null ? { confidence: evidence.confidence } : {}),
        source: evidence.kind === 'screen' ? 'screenshare' : 'webcam',
      })),
    hasActiveCamera: projection.modalities.webcam.active,
    hasActiveScreenShare: projection.modalities.screen.active,
  }
}

/**
 * Browser view and UI-preference store.
 *
 * Canonical session data is accepted only as committed projection envelopes.
 */
export class ClientSessionViewStore {
  private state: ClientSessionViewState = { transcript: [] }
  private listeners = new Set<Listener>()
  private locationPromise: Promise<Location | undefined> | null = null
  private browserLocationCache: Location | undefined
  private projectionStore: ClientSessionStore | null = null

  constructor() {
    if (typeof window !== 'undefined') {
      this.hydrateUiPreferences()
    }
  }

  subscribe(listener: Listener) {
    this.listeners.add(listener)
    return () => this.listeners.delete(listener)
  }

  private notify() {
    for (const listener of this.listeners) listener(this.state)
  }

  private persistUiPreferences() {
    if (typeof window === 'undefined') return
    try {
      const preferences: StoredUiPreferences = {
        ...(this.state.language ? { language: this.state.language } : {}),
        ...(this.state.uiLocale ? { uiLocale: this.state.uiLocale } : {}),
        ...(this.state.uiLocaleExplicit !== undefined
          ? { uiLocaleExplicit: this.state.uiLocaleExplicit }
          : {}),
        ...(this.state.livePolicy ? { livePolicy: this.state.livePolicy } : {}),
      }
      localStorage.setItem(UI_LOCALE_STORAGE_KEY, JSON.stringify(preferences))
    } catch (error) {
      logger.warn('[ClientSessionViewStore] Failed to persist UI preferences', {
        error: error instanceof Error ? error.message : String(error),
      })
    }
  }

  private hydrateUiPreferences() {
    if (typeof window === 'undefined') return
    try {
      const stored = localStorage.getItem(UI_LOCALE_STORAGE_KEY)
      if (!stored) return

      const parsed: unknown = JSON.parse(stored)
      if (!parsed || typeof parsed !== 'object') return
      const preferences = parsed as Record<string, unknown>

      if (typeof preferences.language === 'string') {
        this.state.language = preferences.language
      }
      if (
        typeof preferences.uiLocale === 'string' &&
        isSupportedUiLocale(preferences.uiLocale)
      ) {
        this.state.uiLocale = preferences.uiLocale
      }
      if (typeof preferences.uiLocaleExplicit === 'boolean') {
        this.state.uiLocaleExplicit = preferences.uiLocaleExplicit
      }
      if (preferences.livePolicy && typeof preferences.livePolicy === 'object') {
        const fallback = createDefaultLiveSessionPolicy(
          this.state.language ? { languageCode: this.state.language } : undefined
        )
        this.state.livePolicy = mergeLiveSessionPolicy(
          fallback,
          preferences.livePolicy as Partial<LiveSessionPolicy>
        )
        this.state.language = this.state.livePolicy.languageCode
      }
    } catch (error) {
      logger.warn('[ClientSessionViewStore] Failed to hydrate UI preferences', {
        error: error instanceof Error ? error.message : String(error),
      })
    }
  }

  public clear() {
    const preservedSessionId = this.state.sessionId
    const preservedLanguage = this.state.language
    const preservedUiLocale = this.state.uiLocale
    const preservedUiLocaleExplicit = this.state.uiLocaleExplicit
    const preservedLivePolicy = this.state.livePolicy
    this.state = {
      transcript: [],
      ...(preservedSessionId ? { sessionId: preservedSessionId } : {}),
      ...(preservedLanguage ? { language: preservedLanguage } : {}),
      ...(preservedUiLocale ? { uiLocale: preservedUiLocale } : {}),
      ...(preservedUiLocaleExplicit !== undefined
        ? { uiLocaleExplicit: preservedUiLocaleExplicit }
        : {}),
      ...(preservedLivePolicy ? { livePolicy: preservedLivePolicy } : {}),
    }
    this.projectionStore = preservedSessionId
      ? new ClientSessionStore(preservedSessionId)
      : null
    this.browserLocationCache = undefined
    this.locationPromise = null
    this.notify()
  }

  /**
   * Clear persona-scoped state (research, intelligence, transcript) without
   * changing the active session id. Session id ownership lives in useSessionId().
   */
  public resetPersonaContext() {
    const preservedSessionId = this.state.sessionId
    const preservedLanguage = this.state.language
    const preservedUiLocale = this.state.uiLocale
    const preservedUiLocaleExplicit = this.state.uiLocaleExplicit
    const preservedLivePolicy = this.state.livePolicy

    this.state = {
      transcript: [],
      ...(preservedSessionId ? { sessionId: preservedSessionId } : {}),
      ...(preservedLanguage ? { language: preservedLanguage } : {}),
      ...(preservedUiLocale ? { uiLocale: preservedUiLocale } : {}),
      ...(preservedUiLocaleExplicit !== undefined
        ? { uiLocaleExplicit: preservedUiLocaleExplicit }
        : {}),
      ...(preservedLivePolicy ? { livePolicy: preservedLivePolicy } : {}),
    }
    this.projectionStore?.clear()
    clearCachedDiscoveryReport()
    this.browserLocationCache = undefined
    this.locationPromise = null
    this.notify()
  }

  public clearStorage() {
    if (typeof window !== 'undefined') {
      try {
        localStorage.removeItem(UI_LOCALE_STORAGE_KEY)
        logger.debug('[ClientSessionViewStore] UI preferences cleared')
      } catch (error) {
        logger.error(
          '[ClientSessionViewStore] Failed to clear UI preferences',
          error instanceof Error ? error : new Error(String(error))
        )
      }
    }
  }

  getSnapshot(): ClientSessionViewState {
    return { ...this.state, transcript: [...this.state.transcript] }
  }

  /**
   * Point the open view at another session id without clearing the transcript.
   * setSessionId is for a real visitor change and wipes the thread.
   */
  adoptSessionId(sessionId: string) {
    if (this.state.sessionId === sessionId) return
    this.state = {
      ...this.state,
      sessionId,
      transcript: [...this.state.transcript],
    }
    this.projectionStore = sessionId ? new ClientSessionStore(sessionId) : null
    this.browserLocationCache = undefined
    this.locationPromise = null
    this.notify()
  }

  setSessionId(sessionId: string) {
    if (this.state.sessionId === sessionId) return
    const { language, uiLocale, uiLocaleExplicit, livePolicy } = this.state
    this.state = {
      transcript: [],
      sessionId,
      ...(language ? { language } : {}),
      ...(uiLocale ? { uiLocale } : {}),
      ...(uiLocaleExplicit !== undefined ? { uiLocaleExplicit } : {}),
      ...(livePolicy ? { livePolicy } : {}),
    }
    this.projectionStore = sessionId ? new ClientSessionStore(sessionId) : null
    clearCachedDiscoveryReport()
    this.browserLocationCache = undefined
    this.locationPromise = null
    this.notify()
  }

  acceptServerProjection(envelope: SessionProjectionEnvelopeV1): boolean {
    if (!this.state.sessionId || this.state.sessionId !== envelope.sessionId) {
      return false
    }
    if (!this.projectionStore) {
      this.projectionStore = new ClientSessionStore(this.state.sessionId)
    }
    if (!this.projectionStore.acceptServerEnvelope(envelope)) {
      return false
    }

    const projection = envelope.projection
    const projectedLocation =
      projection.location.latitude !== null && projection.location.longitude !== null
        ? {
            latitude: projection.location.latitude,
            longitude: projection.location.longitude,
          }
        : undefined

    const { language, uiLocale, uiLocaleExplicit, livePolicy } = this.state
    const derivedTranscript = deriveTranscript(projection)
    const withReport =
      projection.report.status === 'completed'
        ? mergeCachedDiscoveryReportIntoTranscript(projection.sessionId, derivedTranscript)
        : derivedTranscript
    const transcript = mergeLiveOptimisticTranscript(withReport, this.state.transcript)
    this.state = {
      sessionId: projection.sessionId,
      transcript,
      researchContext: deriveResearchContext(projection),
      intelligenceContext: deriveIntelligenceContext(projection),
      conversationFlow: deriveConversationFlow(projection),
      multimodalContext: deriveMultimodalContext(projection),
      sessionPermissions: {
        voice: projection.access.permissions.voice,
        webcam: projection.access.permissions.webcam,
        screen: projection.access.permissions.screen,
        files: projection.access.permissions.files,
        location: projection.access.permissions.location,
        research: projection.access.permissions.research,
      },
      ...(projectedLocation ? { location: projectedLocation } : {}),
      ...(language ? { language } : {}),
      ...(uiLocale ? { uiLocale } : {}),
      ...(uiLocaleExplicit !== undefined ? { uiLocaleExplicit } : {}),
      ...(livePolicy ? { livePolicy } : {}),
    }
    this.notify()
    return true
  }

  /**
   * Voice sends a full projection after each turn. acceptServerProjection also
   * rebuilds the transcript, and that replay put the canonical rows above the
   * live rows (sess_52cbe224, the #330 order). Keep the facts. Leave the
   * full-accept watermark alone so a later chat or brief at this version
   * still rebuilds the rows.
   */
  acceptLiveSessionFacts(envelope: SessionProjectionEnvelopeV1): boolean {
    if (!this.state.sessionId || this.state.sessionId !== envelope.sessionId) {
      return false
    }
    if (!this.projectionStore) {
      this.projectionStore = new ClientSessionStore(this.state.sessionId)
    }
    if (!this.projectionStore.acceptFactsEnvelope(envelope)) {
      return false
    }
    this.notify()
    return true
  }

  getServerProjectionSnapshot(): SessionProjectionEnvelopeV1 | null {
    return this.projectionStore?.getSnapshot() ?? null
  }

  /** Replace the memory-only optimistic transcript until a server projection supersedes it. */
  replaceOptimisticTranscript(transcript: TranscriptItem[]) {
    const sessionId = this.state.sessionId
    const next = sessionId
      ? mergeCachedDiscoveryReportIntoTranscript(sessionId, transcript)
      : transcript
    this.state.transcript = [...next]
    this.notify()
  }

  /** Update the memory-only optimistic transcript view. */
  updateOptimisticTranscript(updater: (current: TranscriptItem[]) => TranscriptItem[]) {
    this.replaceOptimisticTranscript(updater([...this.state.transcript]))
  }

  /** Append or revise one memory-only optimistic transcript item by UI id. */
  upsertOptimisticTranscriptItem(item: TranscriptItem) {
    this.updateOptimisticTranscript(current => {
      const existingIndex = current.findIndex(entry => entry.id === item.id)
      if (existingIndex >= 0) {
        const next = [...current]
        next[existingIndex] = { ...current[existingIndex], ...item }
        return next
      }
      return [...current, item]
    })
  }

  /**
   * Acquire browser coordinates for an explicit coordinator command.
   * The result is an ephemeral browser cache and never mutates canonical view state.
   */
  async acquireBrowserLocation(): Promise<Location | undefined> {
    if (this.browserLocationCache) return this.browserLocationCache
    if (this.locationPromise) return this.locationPromise

    this.locationPromise = new Promise<Location | undefined>(resolve => {
      if (typeof navigator === 'undefined' || !navigator.geolocation) {
        resolve(undefined)
        return
      }

      const timeout = setTimeout(() => resolve(undefined), 1000)

      navigator.geolocation.getCurrentPosition(
        position => {
          clearTimeout(timeout)
          const loc = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          }
          this.browserLocationCache = loc
          resolve(loc)
        },
        () => {
          // Silently handle geolocation errors (user denied, timeout, etc.)
          // Don't log as error - this is expected behavior
          clearTimeout(timeout)
          resolve(undefined)
        },
        { timeout: 5000, maximumAge: 1800000 } // Increased timeout to 5s
      )
    }).finally(() => {
      this.locationPromise = null
    })

    return this.locationPromise
  }

  setLanguage(language: string) {
    this.state.language = language
    if (this.state.livePolicy) {
      this.state.livePolicy = {
        ...this.state.livePolicy,
        languageCode: language,
      }
    }
    this.notify()
    this.persistUiPreferences()
  }

  setUiLocale(locale: UiLocale, options?: { explicit?: boolean }) {
    this.state.uiLocale = locale
    if (options?.explicit === true) {
      this.state.uiLocaleExplicit = true
    } else if (options?.explicit === false) {
      this.state.uiLocaleExplicit = false
    }
    this.patchLivePolicy({ languageCode: uiLocaleToLanguageCode(locale) })
  }

  getUiLocaleExplicit(): boolean {
    return this.state.uiLocaleExplicit === true
  }

  setLivePolicy(policy: LiveSessionPolicy) {
    this.state.livePolicy = { ...policy }
    this.state.language = policy.languageCode
    this.notify()
    this.persistUiPreferences()
  }

  patchLivePolicy(patch: Partial<LiveSessionPolicy>) {
    const current =
      this.state.livePolicy ||
      createDefaultLiveSessionPolicy(
        this.state.language ? { languageCode: this.state.language } : undefined
      )
    this.state.livePolicy = mergeLiveSessionPolicy(current, patch)
    this.state.language = this.state.livePolicy.languageCode
    this.notify()
    this.persistUiPreferences()
  }

  /**
   * Ensure language is set, defaulting to browser language or 'en'
   */
  ensureLanguage(): string {
    if (this.state.livePolicy?.languageCode) return this.state.livePolicy.languageCode
    if (this.state.language) return this.state.language

    // Try to detect from browser
    if (typeof navigator !== 'undefined' && navigator.language) {
      const browserLang = navigator.language.split('-')[0] || 'en'
      this.state.language = browserLang
      this.persistUiPreferences()
      return browserLang
    }

    this.state.language = 'en'
    this.persistUiPreferences()
    return 'en'
  }
}

export const clientSessionViewStore = new ClientSessionViewStore()
